import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LabelFormDialog from "./LabelFormDialog";
import DeleteLabelDialog from "./DeleteLabelDialog";
import {
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  PROFILE_COLOR,
  USAGE_TYPES,
  getUsageTotal,
  normalizeText,
} from "./labelUtils";

const StatCard = ({ icon, label, value, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 2,
      flex: 1,
      minWidth: 160,
      borderRadius: 3,
      border: "2px solid",
      borderColor: active ? color : "divider",
      cursor: "pointer",
      transition: "all .15s ease",
      "&:hover": { borderColor: color, transform: "translateY(-1px)" },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: `${color}1A`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const UsageChips = ({ usage }) => {
  const used = USAGE_TYPES.filter(({ key }) => (usage?.[key] || 0) > 0);
  if (!used.length) {
    return <Chip label="Sin uso" size="small" variant="outlined" sx={{ color: "text.secondary" }} />;
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {used.map(({ key, label, Icon }) => (
        <Tooltip key={key} title={label} arrow>
          <Chip
            icon={<Icon sx={{ fontSize: 16 }} />}
            label={usage[key]}
            size="small"
            variant="outlined"
            sx={{ borderColor: "divider" }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

const TypeChip = ({ isProfile }) => (
  <Chip
    label={isProfile ? "Perfil" : "Interés"}
    size="small"
    sx={{
      fontWeight: 600,
      bgcolor: isProfile ? `${PROFILE_COLOR}1A` : `${BRAND_COLOR}1A`,
      color: isProfile ? PROFILE_COLOR : BRAND_COLOR_DARK,
    }}
  />
);

const LabelsTab = ({ labels, isLoading, onChanged, onNotify }) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [usageFilter, setUsageFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formState, setFormState] = useState({ open: false, label: null });
  const [labelToDelete, setLabelToDelete] = useState(null);

  const stats = useMemo(
    () => ({
      total: labels.length,
      profile: labels.filter((l) => l.isProfile).length,
      interest: labels.filter((l) => !l.isProfile).length,
      unused: labels.filter((l) => getUsageTotal(l.usage) === 0).length,
    }),
    [labels]
  );

  const filteredLabels = useMemo(() => {
    const term = normalizeText(search);
    const result = labels.filter((label) => {
      if (term && !normalizeText(label.name).includes(term)) return false;
      if (typeFilter === "profile" && !label.isProfile) return false;
      if (typeFilter === "interest" && label.isProfile) return false;
      const total = getUsageTotal(label.usage);
      if (usageFilter === "used" && total === 0) return false;
      if (usageFilter === "unused" && total > 0) return false;
      return true;
    });

    return [...result].sort((a, b) => {
      if (sortBy === "usage") return getUsageTotal(b.usage) - getUsageTotal(a.usage);
      if (sortBy === "recent") return new Date(b.updatedAt) - new Date(a.updatedAt);
      return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
    });
  }, [labels, search, typeFilter, usageFilter, sortBy]);

  const pagedLabels = filteredLabels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const applyStatFilter = (type, usage) => {
    setTypeFilter(type);
    setUsageFilter(usage);
    setPage(0);
  };

  const handleSaved = async (message) => {
    setFormState({ open: false, label: null });
    onNotify(message, "success");
    await onChanged();
  };

  const handleDeleted = async (message) => {
    setLabelToDelete(null);
    onNotify(message, "success");
    await onChanged();
  };

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<LocalOfferOutlinedIcon />}
          label="Etiquetas totales"
          value={stats.total}
          color={BRAND_COLOR}
          active={typeFilter === "all" && usageFilter === "all"}
          onClick={() => applyStatFilter("all", "all")}
        />
        <StatCard
          icon={<LocalOfferOutlinedIcon />}
          label="De interés"
          value={stats.interest}
          color={BRAND_COLOR_DARK}
          active={typeFilter === "interest" && usageFilter === "all"}
          onClick={() => applyStatFilter("interest", "all")}
        />
        <StatCard
          icon={<BadgeOutlinedIcon />}
          label="De perfil"
          value={stats.profile}
          color={PROFILE_COLOR}
          active={typeFilter === "profile" && usageFilter === "all"}
          onClick={() => applyStatFilter("profile", "all")}
        />
        <StatCard
          icon={<LinkOffIcon />}
          label="Sin uso"
          value={stats.unused}
          color="#F59E0B"
          active={typeFilter === "all" && usageFilter === "unused"}
          onClick={() => applyStatFilter("all", "unused")}
        />
      </Stack>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          sx={{ p: 2 }}
        >
          <TextField
            placeholder="Buscar etiqueta..."
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={usageFilter}
            onChange={(_, value) => {
              if (value) {
                setUsageFilter(value);
                setPage(0);
              }
            }}
          >
            <ToggleButton value="all">Todas</ToggleButton>
            <ToggleButton value="used">En uso</ToggleButton>
            <ToggleButton value="unused">Sin uso</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Ordenar por"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="name">Nombre (A-Z)</MenuItem>
            <MenuItem value="usage">Más usadas</MenuItem>
            <MenuItem value="recent">Modificadas recientemente</MenuItem>
          </TextField>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setFormState({ open: true, label: null })}
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: BRAND_COLOR_DARK }, whiteSpace: "nowrap" }}
          >
            Nueva etiqueta
          </Button>
        </Stack>

        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", bgcolor: "grey.50" } }}>
                <TableCell>Etiqueta</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Dónde se usa</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && labels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : pagedLabels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {labels.length === 0
                        ? "Aún no hay etiquetas registradas."
                        : "No hay etiquetas que coincidan con los filtros."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedLabels.map((label) => (
                  <TableRow key={label.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{label.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getUsageTotal(label.usage)} uso{getUsageTotal(label.usage) === 1 ? "" : "s"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TypeChip isProfile={label.isProfile} />
                    </TableCell>
                    <TableCell>
                      <UsageChips usage={label.usage} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar" arrow>
                        <IconButton onClick={() => setFormState({ open: true, label })} size="small">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar" arrow>
                        <IconButton onClick={() => setLabelToDelete(label)} size="small" color="error">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredLabels.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      <LabelFormDialog
        open={formState.open}
        label={formState.label}
        existingLabels={labels}
        onClose={() => setFormState({ open: false, label: null })}
        onSaved={handleSaved}
      />

      <DeleteLabelDialog
        open={Boolean(labelToDelete)}
        label={labelToDelete}
        onClose={() => setLabelToDelete(null)}
        onDeleted={handleDeleted}
      />
    </Box>
  );
};

export default LabelsTab;
